/**
 * Route suggestion presentation (tracker item 41, phase 2). Pure functions,
 * unit-tested in suggestionUi.test.ts. The backend sends reasons as
 * `{ code, params }`; these turn them into sentences.
 */

import * as types from '../api/types';
import { formatDuration, formatTime } from './dispatchUi';

const unitLabel = (unit: unknown) => (unit === 'KM' ? 'km' : 'mi');

function distanceText(params: types.SuggestionReason['params']): string {
  return typeof params.distance === 'number' ? `${params.distance} ${unitLabel(params.distanceUnit)}` : '';
}

function minutesText(value: unknown): string {
  return typeof value === 'number' ? `${value} min` : '';
}

function endLabel(end: unknown): string {
  switch (end) {
    case 'JOB': return "this job's property";
    case 'DEPOT': return 'the company address (start of the day)';
    case 'PREVIOUS_JOB': return 'the previous job';
    case 'NEXT_JOB': return 'the next job';
    default: return 'a stop';
  }
}

function hours(minutes: unknown): string {
  return typeof minutes === 'number' ? formatDuration(minutes) : '?';
}

/** One reason as a sentence. Unknown codes fall back to the code itself. */
export function suggestionReasonText(reason: types.SuggestionReason): string {
  const p = reason.params ?? {};
  switch (reason.code) {
    case 'ROUTE_MATCH':
      return `On route ${p.routeName ?? 'this route'} (${p.source === 'DATED' ? 'dated coverage' : 'usual route'})`;
    case 'ROUTE_MISMATCH':
      return `Not covering route ${p.routeName ?? 'this route'} that day (${p.source === 'DATED' ? 'dated coverage elsewhere' : 'usual routes elsewhere'})`;
    case 'NEAR_JOB': {
      const where = p.position === 'AFTER' ? 'before' : 'after';
      return `${distanceText(p)} from ${p.customerName || 'another job'} (${minutesText(p.travelMinutes)} drive, ${where} that visit)`;
    }
    case 'FROM_DEPOT':
      return p.known === false
        ? `First visit of the day; distance from the company address unknown (allowing ${minutesText(p.travelMinutes)})`
        : `First visit of the day, ${distanceText(p)} from the company address (${minutesText(p.travelMinutes)} drive)`;
    case 'LIGHT_DAY':
      return `Light day: ${hours(p.bookedMinutes)} booked of ${hours(p.availableMinutes)}`;
    case 'BUSY_DAY':
      return `Busy day: ${hours(p.bookedMinutes)} booked of ${hours(p.availableMinutes)}`;
    case 'EARLIEST_AVAILABLE':
      return 'Earliest available slot';
    case 'APPROXIMATE_LOCATION':
      return `Approximate location for ${endLabel(p.end)} (postal code only)`;
    case 'LOCATION_UNKNOWN':
      return `No map location for ${endLabel(p.end)}; travel there is estimated`;
    default:
      return String(reason.code);
  }
}

/** "Tue 4 Mar · 09:15 - 10:15" style heading for a candidate. */
export function candidateTimeText(candidate: Pick<types.SuggestionCandidate, 'startTime' | 'endTime'>): string {
  return `${formatTime(candidate.startTime)} - ${formatTime(candidate.endTime)}`;
}

/** "+N min travel" (0 when it adds none). */
export function addedTravelText(candidate: Pick<types.SuggestionCandidate, 'addedTravelMinutes'>): string {
  return candidate.addedTravelMinutes > 0 ? `+${candidate.addedTravelMinutes} min travel` : 'No extra travel';
}

/** Why the list is empty, from the `excluded` counts (pairs of technician and date). */
export function excludedText(excluded: types.SuggestionExcluded): string {
  const parts: string[] = [];
  const add = (count: number, singular: string, plural = `${singular}s`) => {
    if (count > 0) parts.push(`${count} ${count === 1 ? singular : plural}`);
  };
  add(excluded.notWorking, 'non-working day');
  add(excluded.timeOff, 'day off', 'days off');
  add(excluded.pastDate, 'past date');
  add(excluded.noGapFits, 'day with no free time long enough', 'days with no free time long enough');
  add(excluded.unavailable, 'day with the assigned technician inactive', 'days with the assigned technician inactive');
  add(excluded.conflicts, 'slot with a conflict', 'slots with a conflict');
  return parts.length ? `Nothing fits. Checked: ${parts.join(', ')}.` : 'Nothing fits in this range.';
}

/** The origin sent with the schedule save (audit). */
export function suggestionOrigin(candidate: types.SuggestionCandidate): types.ScheduleOrigin {
  return { kind: 'AUTO_ASSIGN', score: candidate.score, reasons: candidate.reasons.map((r) => ({ code: r.code, params: { ...r.params } })) };
}
